import { useState, useEffect, useMemo } from 'react';
import { supabase } from '../lib/supabase';
import { buildMatchIndex, normalizeSubgenreName } from '../lib/genreTaxonomy';

// What a reader sees when adding their own subgenre fails. The database's
// own wording ("new row violates row-level security policy ...") means
// nothing to them, so it goes to the console and this goes on screen.
const PENDING_SUBGENRE_FAILED = "We couldn't add that subgenre just now. Try again, or pick one from the list.";

/**
 * Shared with reanlo-author (byte-for-byte port, PR3,
 * docs/genre-subgenre-build-spec.md) -- see src/lib/genreTaxonomy.js's own
 * header for why. Loads the 20-genre / ~183-subgenre vocabulary once per
 * mount and builds the did-you-mean match index from it
 * (src/lib/genreTaxonomy.js). Kept separate from that file on purpose --
 * genreTaxonomy.js stays import-free of Supabase/React so its logic runs
 * under plain node with no stub (see that file's own docblock); this hook is
 * the one place that actually fetches. Powers both controls here: Control B
 * (GenreSubgenreFilter) only reads genres/canonSubgenresForGenre; Control A
 * surfaces also use matchIndex/upsertPendingSubgenre for the write-in path.
 *
 * Genres are ordered by sort_order (PR1's migration seeds it) so the picker
 * renders in the same order the taxonomy doc lists them, not alphabetical.
 *
 * Subgenres: fetches EVERY row, not just status='canon'. The picker's
 * "what can I choose" list filters to canon itself (a pending write-in is
 * not offered to a second author until promoted -- Part 5, decision 9), but
 * a PENDING row already attached to THIS book (via initialBook.book_subgenres)
 * still needs a name to render its chip, and upsertPendingSubgenre below
 * needs to read back a row it just inserted regardless of status. Fetching
 * once and filtering in the picker is one round trip instead of two.
 */
export function useGenreTaxonomy() {
  const [genres, setGenres] = useState([]);
  const [subgenres, setSubgenres] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    let cancelled = false;
    (async () => {
      const [{ data: genreRows, error: gErr }, { data: subgenreRows, error: sErr }] = await Promise.all([
        supabase.from('genres').select('id, name, sort_order').order('sort_order', { ascending: true }),
        supabase.from('subgenres').select('id, genre_id, name, normalized_name, status, alias_of'),
      ]);
      if (cancelled) return;
      if (gErr || sErr) {
        setError(gErr?.message || sErr?.message || 'Could not load the genre list.');
        setLoading(false);
        return;
      }
      setGenres(genreRows || []);
      setSubgenres(subgenreRows || []);
      setLoading(false);
    })();
    return () => { cancelled = true; };
  }, []);

  const genresById = useMemo(() => new Map(genres.map(g => [g.id, g])), [genres]);
  const subgenresById = useMemo(() => new Map(subgenres.map(s => [s.id, s])), [subgenres]);
  const matchIndex = useMemo(() => buildMatchIndex(subgenres, genresById), [subgenres, genresById]);

  // Canon subgenres for one genre, the actual "what can I pick" list --
  // never includes alias rows (they exist only to be matched against, per
  // buildMatchIndex's own docblock) or another author's unpromoted write-in.
  function canonSubgenresForGenre(genreId) {
    return subgenres.filter(s => s.genre_id === genreId && s.status === 'canon' && !s.alias_of);
  }

  /**
   * Create (or find) a pending write-in and return its id. Upsert on the
   * UNIQUE (genre_id, normalized_name) key so the "one row forever"
   * guarantee (Part 5, decision 9) comes from the database constraint, not
   * from this function being careful -- a second author typing the exact
   * same wording lands on the same row automatically, no matcher needed for
   * that part.
   *
   * normalized_name is a GENERATED column (PR1's migration) -- never set it
   * here, Postgres computes it from `name` with the exact same function
   * normalizeSubgenreName mirrors client-side.
   *
   * RLS: "Authenticated can propose subgenres" (PR1's migration) permits
   * exactly this shape -- WITH CHECK (status = 'pending' AND alias_of IS NULL).
   */
  async function upsertPendingSubgenre(genreId, name) {
    const trimmed = (name || '').trim();
    if (!trimmed) return { error: 'Type a name first.' };

    const { error: upsertErr } = await supabase
      .from('subgenres')
      .upsert(
        { genre_id: genreId, name: trimmed, status: 'pending' },
        { onConflict: 'genre_id,normalized_name', ignoreDuplicates: true },
      );
    if (upsertErr) { console.error('pending subgenre insert failed:', upsertErr.message); return { error: PENDING_SUBGENRE_FAILED }; }

    // ignoreDuplicates means a pre-existing row (canon OR pending, from this
    // author or any other) returns no data from the upsert itself -- read
    // it back by the same key the constraint enforces, not by assuming the
    // upsert's own response shape.
    const normalized = normalizeSubgenreName(trimmed);
    const { data: row, error: readErr } = await supabase
      .from('subgenres')
      .select('id, genre_id, name, status, alias_of')
      .eq('genre_id', genreId)
      .eq('normalized_name', normalized)
      .maybeSingle();
    if (readErr || !row) { console.error('pending subgenre read-back failed:', readErr?.message || 'no row'); return { error: PENDING_SUBGENRE_FAILED }; }

    // The row on that key may have been merged since this page loaded -- the
    // admin queue's merge keeps it as an alias (alias_of set) rather than
    // deleting it. book_subgenres must point at the canonical row, never an
    // alias, so follow alias_of to it. Normally one hop; the loop and the
    // seen-set only exist so a chain left by an earlier merge still resolves
    // and a cycle cannot spin. The match index catches this case too, but
    // only if the page loaded after the merge -- this is the path taken when
    // it did not.
    let resolved = row;
    const seen = new Set([row.id]);
    while (resolved.alias_of) {
      if (seen.has(resolved.alias_of)) return { error: PENDING_SUBGENRE_FAILED };
      seen.add(resolved.alias_of);
      const { data: target, error: targetErr } = await supabase
        .from('subgenres')
        .select('id, genre_id, name, status, alias_of')
        .eq('id', resolved.alias_of)
        .maybeSingle();
      if (targetErr || !target) { console.error('pending subgenre alias follow failed:', targetErr?.message || 'no row'); return { error: PENDING_SUBGENRE_FAILED }; }
      resolved = target;
    }

    // Keep both rows locally, so the match index resolves this wording
    // itself from now on instead of coming back through here.
    setSubgenres(prev => {
      let next = prev;
      for (const r of [row, resolved]) if (!next.some(s => s.id === r.id)) next = [...next, r];
      return next;
    });
    return { data: resolved };
  }

  return {
    genres, subgenres, genresById, subgenresById, matchIndex,
    loading, error,
    canonSubgenresForGenre, upsertPendingSubgenre,
  };
}
